import type { IconType } from "react-icons";
import { FiFileText, FiLayers, FiList, FiPlus, FiSearch, FiZap } from "react-icons/fi";
import type { AppRole } from "../../common/layout/navConfig";
import type { SubAccess } from "../../common/subscription/useSub";
import { ProjectStatus } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { QUEUE_LINK } from "./panels/NeedsWaitingPanel";

export type HeroActionTarget = { kind: "navigate"; to: string } | { kind: "action"; action: "createSourcingEvent" | "createNeed" };
export type HeroMetric = "needsQueue" | "activeContracts";
export type PanelKind =
  | "sourcingEvents"
  | "myNeeds"
  | "activeContracts"
  | "invitations"
  | "myBids"
  | "openProcurements"
  | "upcomingDeadlines"
  | "contractExpiry"
  | "procurementPhases"
  | "recentContracts"
  | "ongoingProjects"
  | "needsWaiting";

export interface HeroAction {
  key: string;
  labelKey: string;
  sublabelKey: string;        // static fallback text; metric overrides it when set
  icon: IconType;
  variant: "primary" | "default";
  target: HeroActionTarget;
  metric?: HeroMetric;
  /** Gate the action on a `useSub()` flag (role-based view access). */
  access?: keyof SubAccess;
}
export interface HomeConfig {
  headlineKey: string;
  heroActions: HeroAction[];
  panels: PanelKind[];
}

/**
 * The domain each panel belongs to, as a `useSub()` gate. A viewer role only sees the panels of its
 * own domain (admins and owners hold every view role); panels without an entry are ungated.
 */
export const panelAccess: Partial<Record<PanelKind, keyof SubAccess>> = {
  sourcingEvents: "canViewSourcingEvents",
  upcomingDeadlines: "canViewSourcingEvents",
  procurementPhases: "canViewSourcingEvents",
  myNeeds: "canReportNeeds",
  needsWaiting: "canAssessNeeds",
  activeContracts: "canViewContracts",
  contractExpiry: "canViewContracts",
  recentContracts: "canViewContracts",
  ongoingProjects: "canViewProjects",
  invitations: "canBid",
  myBids: "canBid",
  openProcurements: "canBid",
};

const HERO_SLOTS = 3;

export const filterHomeConfig = (config: HomeConfig, sub: Partial<SubAccess>): HomeConfig => ({
  ...config,
  heroActions: leadWithPrimary(
    config.heroActions.filter((action) => !action.access || sub[action.access]).slice(0, HERO_SLOTS),
  ),
  panels: config.panels.filter((panel) => {
    const access = panelAccess[panel];
    return !access || sub[access];
  }),
});

const leadWithPrimary = (actions: HeroAction[]): HeroAction[] =>
  actions.some((action) => action.variant === "primary")
    ? actions
    : actions.map((action, index) => (index === 0 ? { ...action, variant: "primary" } : action));

const CHART_PANELS = ["contractExpiry", "procurementPhases"] as const satisfies readonly PanelKind[];
const BIDDING_PANELS = ["invitations", "myBids", "openProcurements"] as const satisfies readonly PanelKind[];

const BROWSE_PROCUREMENTS: HeroAction = { key: "browse", labelKey: "Find procurements", sublabelKey: "Browse published procurements", icon: FiSearch, variant: "default", target: { kind: "navigate", to: urls.app.supplier.procurements }, access: "canBid" };
const MY_INVITATIONS: HeroAction = { key: "invitations", labelKey: "My invitations", sublabelKey: "Invitations to submit a bid", icon: FiList, variant: "default", target: { kind: "navigate", to: urls.app.supplier.invitations }, access: "canBid" };

export const homeConfig: Record<AppRole, HomeConfig> = {
  advisor: {
    headlineKey: "What will you work on today?",
    heroActions: [
      { key: "start", labelKey: "Start procurement", sublabelKey: "Create and announce", icon: FiPlus, variant: "primary", target: { kind: "action", action: "createSourcingEvent" }, access: "canCreateEvents" },
      { key: "assess", labelKey: "Assess need", sublabelKey: "Review the needs in the queue", icon: FiZap, variant: "default", target: { kind: "navigate", to: QUEUE_LINK }, metric: "needsQueue", access: "canAssessNeeds" },
      { key: "agreements", labelKey: "Active agreements", sublabelKey: "Your live contracts", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.app.contracts.active }, metric: "activeContracts", access: "canViewContracts" },
    ],
    panels: [...CHART_PANELS, "needsWaiting", "upcomingDeadlines", "sourcingEvents", "recentContracts"],
  },
  orderer: {
    headlineKey: "What are you looking for?",
    heroActions: [
      { key: "report", labelKey: "Report need", sublabelKey: "Register a new need", icon: FiPlus, variant: "primary", target: { kind: "action", action: "createNeed" }, access: "canReportNeeds" },
      { key: "mine", labelKey: "My needs", sublabelKey: "Your registered needs", icon: FiList, variant: "default", target: { kind: "navigate", to: urls.app.needs.mine }, access: "canReportNeeds" },
      { key: "agreements", labelKey: "Active agreements", sublabelKey: "Your live contracts", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.app.contracts.active }, metric: "activeContracts", access: "canViewContracts" },
      { key: "projects", labelKey: "Projects", sublabelKey: "Your ongoing projects", icon: FiLayers, variant: "default", target: { kind: "navigate", to: `${urls.app.projects.viewAll}?status=${ProjectStatus.Ongoing}` }, access: "canViewProjects" },
    ],
    // The sourcing panels only appear for orderers whose roles include sourcing view access — a
    // sourcing viewer sees the whole procurement picture, just without the create/edit entries.
    panels: [
      ...CHART_PANELS,
      "myNeeds",
      "sourcingEvents",
      "upcomingDeadlines",
      "activeContracts",
      "recentContracts",
      "ongoingProjects",
    ],
  },
  supplier: {
    headlineKey: "Find your next opportunity",
    heroActions: [
      { ...BROWSE_PROCUREMENTS, variant: "primary" },
      { key: "bids", labelKey: "My bids", sublabelKey: "Your offers in progress", icon: FiFileText, variant: "default", target: { kind: "navigate", to: urls.app.supplier.bids } },
      MY_INVITATIONS,
    ],
    panels: [...BIDDING_PANELS],
  },
};
